<template>
  <div class="mobile-login" :class="{ 'dark-theme': isDark }">
    <!-- 顶部导航 -->
    <header class="mobile-header">
      <button @click="goBack" class="back-btn">
        <i class="fas fa-arrow-left"></i>
      </button>
      <h1>登录</h1>
      <!-- 主题切换按钮 -->
      <div class="theme-toggle" @click="toggleTheme">
        <i :class="isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
      </div>
    </header>

    <!-- 主要内容区域 -->
    <main class="login-content">
      <!-- Logo区域 -->
      <div class="logo-section">
        <img src="/assets/shenxiaozhi.png" alt="神小智" class="logo">
        <h2>神小智AI创意视频平台</h2>
        <p>开始您的AI视频创意之旅</p>
      </div>

      <!-- 登录表单 -->
      <div class="login-form">
        <form @submit.prevent="handleLogin">
          <!-- 手机号输入 -->
          <div class="form-group">
            <div class="input-wrapper">
              <i class="fas fa-phone input-icon"></i>
              <input 
                v-model="loginForm.phone" 
                type="tel" 
                placeholder="请输入手机号" 
                maxlength="11"
                class="form-input"
                :class="{ 'error': phoneError }"
                @input="clearPhoneError"
              />
            </div>
            <div v-if="phoneError" class="error-text">{{ phoneError }}</div>
          </div>

          <!-- 验证码输入 -->
          <div class="form-group">
            <div class="input-wrapper">
              <i class="fas fa-shield-alt input-icon"></i>
              <input 
                v-model="loginForm.sms_code" 
                type="text" 
                placeholder="请输入验证码" 
                maxlength="6"
                class="form-input"
                :class="{ 'error': codeError }"
                @input="clearCodeError"
              />
              <button 
                type="button"
                @click="sendSmsCode" 
                :disabled="countdown > 0 || !loginForm.phone || sendingCode"
                class="sms-btn"
                :class="{ 'active': countdown === 0 && loginForm.phone && !sendingCode }"
              >
                <span v-if="sendingCode">发送中...</span>
                <span v-else-if="countdown > 0">{{ countdown }}s</span>
                <span v-else>{{ smsButtonText }}</span>
              </button>
            </div>
            <div v-if="codeError" class="error-text">{{ codeError }}</div>
          </div>

          <!-- 登录按钮 -->
          <button 
            type="submit" 
            :disabled="loading || !canSubmit"
            class="login-btn"
            :class="{ 'loading': loading }"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <span>{{ loading ? '登录中...' : '登录' }}</span>
          </button>
        </form>


        <!-- 用户协议 -->
        <div class="agreement">
          <label class="checkbox-wrapper">
            <input type="checkbox" v-model="agreeTerms">
            <span class="checkmark"></span>
          </label>
          <span class="agreement-text">
            我已阅读并同意
            <a href="#" @click.prevent="showTerms">《用户协议》</a>
            和
            <a href="#" @click.prevent="showPrivacy">《隐私政策》</a>
          </span>
        </div>
      </div>
    </main>

    <!-- 底部信息 -->
    <footer class="mobile-footer">
      <p>© 2025 神小智AI 版权所有</p>
    </footer>

    <!-- 加载遮罩 -->
    <div v-if="loading" class="loading-overlay">
      <div class="simple-loading">
        <div class="loading-circle"></div>
        <p>登录中...</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from '@/utils/router.js';
import { useUserStoreHook } from '@/store/modules/user';
import { ElMessage } from '@/utils/message.js';
import { useTheme } from '@/composables/useTheme';

const router = useRouter();
const route = useRoute();
const userStore = useUserStoreHook();

// 主题管理
const { isDark, loadTheme, toggleTheme } = useTheme();

// 表单数据
const loginForm = reactive({
  phone: '',
  sms_code: ''
});

// 状态管理
const loading = ref(false);
const sendingCode = ref(false);
const countdown = ref(0);
const agreeTerms = ref(true);
const phoneError = ref('');
const codeError = ref('');
const sentOnce = ref(false);

let countdownTimer: NodeJS.Timeout | null = null;

// 计算属性
const canSubmit = computed(() => {
  return loginForm.phone.length === 11 && 
         loginForm.sms_code.length === 6 && 
         agreeTerms.value;
});

const smsButtonText = computed(() => {
  return sentOnce.value ? '重新发送' : '获取验证码';
});

// 验证手机号格式
const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^1[3-9]\d{9}$/;
  return phoneRegex.test(phone);
};

// 清除错误提示
const clearPhoneError = () => {
  phoneError.value = '';
};

const clearCodeError = () => {
  codeError.value = '';
};

// 发送短信验证码
const sendSmsCode = async () => {
  if (!loginForm.phone) {
    phoneError.value = '请输入手机号';
    return;
  }
  
  if (!validatePhone(loginForm.phone)) {
    phoneError.value = '请输入正确的手机号格式';
    return;
  }
  
  try {
    sendingCode.value = true;
    const response = await userStore.sendSmsCode(loginForm.phone);
    
    if (response && response.success) {
      ElMessage.success('验证码已发送，请查收短信');
      sentOnce.value = true;
      
      // 开始倒计时
      countdown.value = 60;
      countdownTimer = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          if (countdownTimer) {
            clearInterval(countdownTimer);
            countdownTimer = null;
          }
        }
      }, 1000);
    } else {
      const errorMsg = response?.error || '发送验证码失败';
      ElMessage.error(errorMsg);
    }
  } catch (error: any) {
    console.error('发送验证码失败:', error);
    ElMessage.error(error.message || '网络错误，请稍后重试');
  } finally {
    sendingCode.value = false;
  }
};

// 登录处理
const handleLogin = async () => {
  // 验证表单
  if (!loginForm.phone) {
    phoneError.value = '请输入手机号';
    return;
  }

  if (!validatePhone(loginForm.phone)) {
    phoneError.value = '请输入正确的手机号';
    return;
  }

  if (!loginForm.sms_code) {
    codeError.value = '请输入验证码';
    return;
  }

  if (loginForm.sms_code.length !== 6) {
    codeError.value = '验证码应为6位数字';
    return;
  }

  if (!agreeTerms.value) {
    ElMessage.warning('请阅读并同意用户协议和隐私政策');
    return;
  }

  loading.value = true;
  
  try {
    const response = await userStore.login(loginForm);
    
    if (response && response.success) {
      ElMessage.success('登录成功');
      
      // 跳转到指定页面或默认页面
      const redirect = (route.value?.query?.redirect as string) || '/pages/mobile/index';
      router.push(redirect);
    } else {
      const errorMsg = response?.error || '登录失败';
      ElMessage.error(errorMsg);
    }
  } catch (error: any) {
    console.error('登录失败:', error);
    ElMessage.error(error.message || '网络错误，请稍后重试');
  } finally {
    loading.value = false;
  }
};


// 显示条款
const showTerms = () => {
  ElMessage.info('用户协议详情页面开发中');
};

const showPrivacy = () => {
  ElMessage.info('隐私政策详情页面开发中');
};

// 返回上一页
const goBack = () => {
  router.back();
};

// 页面加载时检查登录状态
onMounted(() => {
  // 加载主题设置
  loadTheme();
  
  // 如果用户已经登录，重定向到指定页面
  if (userStore.token) {
    const redirect = route.value?.query?.redirect as string || '/pages/mobile/index';
    // ElMessage.success('您已登录');
    console.log('您已登录');
    router.replace(redirect);
  }
});

// 页面卸载时清理
onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
});
</script>

<style scoped lang="scss">
.mobile-login {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  position: relative;
}

.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 0;
  z-index: 100;
  
  .back-btn {
    width: 40px;
    height: 40px;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    color: white;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateX(-2px);
    }
  }
  
  h1 {
    color: white;
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }
  
  .header-placeholder {
    width: 40px;
  }
}

.login-content {
  flex: 1;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.logo-section {
  text-align: center;
  margin-bottom: 40px;
  
  .logo {
    width: 80px;
    height: 80px;
    border-radius: 20px;
    margin-bottom: 16px;
    object-fit: cover;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
  
  h2 {
    color: white;
    font-size: 24px;
    font-weight: bold;
    margin: 0 0 8px 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 16px;
    margin: 0;
  }
}

.login-form {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.form-group {
  margin-bottom: 20px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  
  .input-icon {
    position: absolute;
    left: 16px;
    color: #999;
    font-size: 16px;
    z-index: 1;
  }
  
  .form-input {
    width: 100%;
    height: 52px;
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 0 16px 0 48px;
    font-size: 16px;
    color: #333;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #667eea;
      background: white;
    }
    
    &.error {
      border-color: #dc3545;
      background: #fff5f5;
    }
    
    &::placeholder {
      color: #999;
    }
  }
  
  .sms-btn {
    position: absolute;
    right: 8px;
    height: 36px;
    padding: 0 16px;
    background: #f1f3f4;
    border: none;
    border-radius: 8px;
    color: #666;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    
    &.active {
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      
      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      }
    }
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
}

.error-text {
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
  margin-left: 8px;
}

.login-btn {
  width: 100%;
  height: 52px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  margin-bottom: 24px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  &.loading {
    background: #ccc;
  }
}


.agreement {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 20px;
  
  .checkbox-wrapper {
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    
    input[type="checkbox"] {
      opacity: 0;
      width: 0;
      height: 0;
    }
    
    .checkmark {
      width: 18px;
      height: 18px;
      background: #f8f9fa;
      border: 2px solid #e9ecef;
      border-radius: 4px;
      position: relative;
      transition: all 0.3s ease;
      
      &::after {
        content: '';
        position: absolute;
        left: 5px;
        top: 2px;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
        opacity: 0;
        transition: opacity 0.3s ease;
      }
    }
    
    input:checked ~ .checkmark {
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-color: transparent;
      
      &::after {
        opacity: 1;
      }
    }
  }
  
  .agreement-text {
    font-size: 14px;
    color: #666;
    line-height: 1.4;
    flex: 1;
    
    a {
      color: #667eea;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.mobile-footer {
  padding: 20px 16px;
  text-align: center;
  background: rgba(0, 0, 0, 0.1);
  
  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
    margin: 0;
  }
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

// 简约加载动画
.simple-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  .loading-circle {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top: 3px solid white;
    border-radius: 50%;
    animation: simpleRotate 1s linear infinite;
    margin-bottom: 16px;
  }
  
  p {
    color: white;
    font-size: 16px;
    margin: 0;
    font-weight: 500;
    opacity: 0.9;
  }
}

// 简单旋转动画
@keyframes simpleRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 响应式设计 */
@media (max-width: 480px) {
  .login-content {
    padding: 16px 12px;
  }
  
  .login-form {
    padding: 24px 20px;
  }
  
  .form-input {
    height: 48px;
    font-size: 16px;
    padding-left: 44px;
  }
  
  .login-btn {
    height: 48px;
    font-size: 16px;
  }
}

/* 输入框自动填充样式 */
.form-input:-webkit-autofill,
.form-input:-webkit-autofill:hover,
.form-input:-webkit-autofill:focus,
.form-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px #f8f9fa inset !important;
  -webkit-text-fill-color: #333 !important;
  caret-color: #333 !important;
}

/* 动画效果 */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.logo-section,
.login-form {
  animation: slideInUp 0.6s ease-out;
}

.login-form {
  animation-delay: 0.2s;
}

// 主题切换按钮
.theme-toggle {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  i {
    font-size: 18px;
    color: white;
    transition: all 0.3s ease;
  }
}

// 暗色主题样式
.mobile-login.dark-theme {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);

  .mobile-header {
    background: rgba(0, 0, 0, 0.4);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    h1 {
      color: #ffffff;
    }

    .back-btn {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
      border: 1px solid rgba(255, 255, 255, 0.2);

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }

    .theme-toggle {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }

  // 强制覆盖所有表单输入框样式 - 移动端专用
  input[type="tel"],
  input[type="text"],
  .input-wrapper,
  .form-group .input-wrapper,
  .login-form .input-wrapper,
  .login-form .form-group .input-wrapper {
    background: rgba(30, 30, 50, 0.9) !important;
    border: 1px solid rgba(30, 30, 50, 0.9) !important;
  }

  input[type="tel"],
  input[type="text"],
  .form-input,
  .input-wrapper .form-input,
  .login-form .form-input,
  .login-form .input-wrapper .form-input {
    background: rgba(30, 30, 50, 0.9) !important;
    color: #ffffff !important;
    border: 1px solid rgba(30, 30, 50, 0.9) !important;

    &:focus {
      background: rgba(30, 30, 50, 1) !important;
      border-color: #4a90e2 !important;
      outline: none !important;
    }

    &.error {
      background: rgba(60, 30, 30, 0.9) !important;
      border-color: #ff6b6b !important;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.6) !important;
    }
  }

  // 覆盖浏览器自动填充样式
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px rgba(30, 30, 50, 0.9) inset !important;
    -webkit-text-fill-color: #ffffff !important;
  }

  // 登录按钮样式
  .login-btn {
    background: linear-gradient(135deg, #2d3748, #4a5568) !important;
    border: 1px solid rgba(30, 30, 50, 0.9) !important;
    color: #e2e8f0 !important;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #4a5568, #2d3748) !important;
      border: 1px solid rgba(30, 30, 50, 1) !important;
    }

    &:disabled {
      background: rgba(30, 30, 50, 0.5) !important;
      color: rgba(255, 255, 255, 0.3) !important;
      border: 1px solid rgba(30, 30, 50, 0.7) !important;
    }
  }

  .login-content {
    .logo-section {
      h2 {
        color: #ffffff;
      }

      p {
        color: rgba(255, 255, 255, 0.8);
      }
    }

    // 登录表单容器暗色主题
    .login-form {
      background: rgba(30, 30, 50, 0.9) !important;
      border: 1px solid rgba(255, 255, 255, 0.1) !important;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3) !important;
    }
  }

  // 验证码按钮样式
  .sms-btn {
    background: linear-gradient(135deg, #2d3748, #4a5568) !important;
    color: #e2e8f0 !important;
    border: 1px solid rgba(30, 30, 50, 0.9) !important;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #4a5568, #2d3748) !important;
      border: 1px solid rgba(30, 30, 50, 1) !important;
    }

    &:disabled {
      background: rgba(30, 30, 50, 0.5) !important;
      color: rgba(255, 255, 255, 0.3) !important;
      border: 1px solid rgba(30, 30, 50, 0.7) !important;
    }
  }

  // 错误文字
  .error-text {
    color: #ff6b6b !important;
  }

  // 自定义复选框和协议文字
  .agreement {
    .checkmark {
      background: rgba(30, 30, 50, 0.8) !important;
      border: 1px solid rgba(30, 30, 50, 0.9) !important;
    }

    input:checked ~ .checkmark {
      background: linear-gradient(135deg, #2d3748, #4a5568) !important;
      border-color: rgba(30, 30, 50, 1) !important;
    }

    .agreement-text {
      color: rgba(255, 255, 255, 0.8) !important;

      a {
        color: #4a90e2 !important;

        &:hover {
          color: #357abd !important;
        }
      }
    }
  }

  // 图标颜色
  .input-icon {
    color: rgba(255, 255, 255, 0.6) !important;
  }

  // 简约加载动画 - 深色主题
  .simple-loading {
    .loading-circle {
      border: 3px solid rgba(255, 255, 255, 0.2) !important;
      border-top: 3px solid rgba(255, 255, 255, 0.8) !important;
    }
    
    p {
      color: rgba(255, 255, 255, 0.9) !important;
    }
  }
}
</style>
